import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, UserCheck, UserX, Download, Upload, FileSpreadsheet, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { Switch } from '@/components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default permissions structure
const DEFAULT_PERMISSIONS = {
  edit_subscribers: false,
  delete_subscribers: false,
  edit_payments: false,
  delete_payments: false,
  edit_invoices: false,
  delete_invoices: false,
  edit_expenses: false,
  delete_expenses: false,
  confirm_deposits: false,
  send_announcements: false,
  manage_users: false
};

// NAS-specific permissions
const DEFAULT_NAS_PERMISSIONS = {
  nas_view_products: false,
  nas_add_products: false,
  nas_edit_products: false,
  nas_delete_products: false,
  nas_view_pos: false,
  nas_use_pos: false,
  nas_view_inventory: false,
  nas_manage_inventory: false,
  nas_view_customers: false,
  nas_manage_customers: false,
  nas_view_orders: false,
  nas_manage_orders: false,
  nas_view_reports: false,
  nas_view_deductions: false
};

// Permission labels for display
const PERMISSION_LABELS = {
  edit_subscribers: 'Edit Subscribers',
  delete_subscribers: 'Delete Subscribers',
  edit_payments: 'Edit Payments',
  delete_payments: 'Delete Payments',
  edit_invoices: 'Edit Invoices',
  delete_invoices: 'Delete Invoices',
  edit_expenses: 'Edit Expenses',
  delete_expenses: 'Delete Expenses',
  confirm_deposits: 'Confirm Deposits',
  send_announcements: 'Send Announcements',
  manage_users: 'Manage Users'
};

// NAS Permission labels
const NAS_PERMISSION_LABELS = {
  nas_view_products: 'View Products',
  nas_add_products: 'Add Products',
  nas_edit_products: 'Edit Products',
  nas_delete_products: 'Delete Products',
  nas_view_pos: 'View POS',
  nas_use_pos: 'Use POS (Sales)',
  nas_view_inventory: 'View Inventory',
  nas_manage_inventory: 'Manage Inventory',
  nas_view_customers: 'View Customers',
  nas_manage_customers: 'Manage Customers',
  nas_view_orders: 'View Orders',
  nas_manage_orders: 'Manage Orders',
  nas_view_reports: 'View Reports',
  nas_view_deductions: 'View Deductions/Incentives'
};

// Group permissions by category for better UI
const PERMISSION_GROUPS = [
  { 
    title: 'Subscribers', 
    permissions: ['edit_subscribers', 'delete_subscribers'] 
  },
  { 
    title: 'Payments', 
    permissions: ['edit_payments', 'delete_payments', 'confirm_deposits'] 
  },
  { 
    title: 'Invoices', 
    permissions: ['edit_invoices', 'delete_invoices'] 
  },
  { 
    title: 'Expenses', 
    permissions: ['edit_expenses', 'delete_expenses'] 
  },
  { 
    title: 'Other', 
    permissions: ['send_announcements', 'manage_users'] 
  }
];

// NAS Permission Groups
const NAS_PERMISSION_GROUPS = [
  {
    title: 'Products',
    permissions: ['nas_view_products', 'nas_add_products', 'nas_edit_products', 'nas_delete_products']
  },
  {
    title: 'Point of Sale',
    permissions: ['nas_view_pos', 'nas_use_pos']
  },
  {
    title: 'Inventory',
    permissions: ['nas_view_inventory', 'nas_manage_inventory']
  },
  {
    title: 'Customers & Orders',
    permissions: ['nas_view_customers', 'nas_manage_customers', 'nas_view_orders', 'nas_manage_orders']
  },
  {
    title: 'Reports & Financials',
    permissions: ['nas_view_reports', 'nas_view_deductions']
  }
];

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    mobile: '',
    full_name: '',
    password: '',
    role: 'staff',
    permissions: { ...DEFAULT_PERMISSIONS },
    nas_access: false, // NAS module access
    nas_permissions: { ...DEFAULT_NAS_PERMISSIONS }, // NAS-specific permissions
    // SR Incentives tracking
    sr_incentives_enabled: false,
    sr_incentives_rate: 0, // Rate per sack for this user
    sr_incentives_total: 0, // Total accumulated incentives
    // Pay settings
    salary_type: 'daily',  // "monthly" or "daily"
    half_month_salary: '',
    daily_rate: '',
    overtime_rate: '',
    hours_per_day: '8',
    sss: '',
    philhealth: '',
    pagibig: '',
    tax: '',
    // Location settings
    assigned_office_id: '',
    clock_in_radius_km: 2,
    allow_remote_clock: false
  });

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
      fetchOffices();
    }
  }, [currentUser]);

  const fetchOffices = async () => {
    try {
      const response = await axios.get(`${API}/offices`);
      setOffices(response.data);
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/users/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Users exported successfully!');
    } catch (error) {
      toast.error('Failed to export users');
    } finally {
      setExporting(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/users/import/excel`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/users/template/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          mobile: formData.mobile,
          full_name: formData.full_name,
          role: formData.role,
          permissions: formData.role === 'admin' ? 
            Object.keys(DEFAULT_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {}) : 
            formData.permissions,
          nas_access: formData.nas_access, // NAS module access
          nas_permissions: formData.role === 'admin' ? 
            Object.keys(DEFAULT_NAS_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {}) : 
            formData.nas_permissions,
          // SR Incentives
          sr_incentives_enabled: formData.sr_incentives_enabled || false,
          sr_incentives_rate: parseFloat(formData.sr_incentives_rate) || 0,
          sr_incentives_total: parseFloat(formData.sr_incentives_total) || 0,
          // Pay settings
          salary_type: formData.salary_type || 'daily',
          half_month_salary: parseFloat(formData.half_month_salary) || 0,
          daily_rate: parseFloat(formData.daily_rate) || 0,
          overtime_rate: parseFloat(formData.overtime_rate) || 0,
          hours_per_day: parseFloat(formData.hours_per_day) || 8,
          sss: parseFloat(formData.sss) || 0,
          philhealth: parseFloat(formData.philhealth) || 0,
          pagibig: parseFloat(formData.pagibig) || 0,
          tax: parseFloat(formData.tax) || 0,
          // Location settings
          assigned_office_id: formData.assigned_office_id || null,
          clock_in_radius_km: parseFloat(formData.clock_in_radius_km) || 2,
          allow_remote_clock: formData.allow_remote_clock
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        const createData = {
          ...formData,
          email: formData.mobile + '@staff.mipay.uk', // Generate email from mobile
          permissions: formData.role === 'admin' ? 
            Object.keys(DEFAULT_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {}) : 
            formData.permissions,
          nas_access: formData.nas_access, // NAS module access
          nas_permissions: formData.role === 'admin' ? 
            Object.keys(DEFAULT_NAS_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {}) : 
            formData.nas_permissions,
          // SR Incentives
          sr_incentives_enabled: formData.sr_incentives_enabled || false,
          sr_incentives_rate: parseFloat(formData.sr_incentives_rate) || 0,
          sr_incentives_total: parseFloat(formData.sr_incentives_total) || 0,
          // Pay settings
          salary_type: formData.salary_type || 'daily',
          half_month_salary: parseFloat(formData.half_month_salary) || 0,
          daily_rate: parseFloat(formData.daily_rate) || 0,
          overtime_rate: parseFloat(formData.overtime_rate) || 0,
          hours_per_day: parseFloat(formData.hours_per_day) || 8,
          sss: parseFloat(formData.sss) || 0,
          philhealth: parseFloat(formData.philhealth) || 0,
          pagibig: parseFloat(formData.pagibig) || 0,
          tax: parseFloat(formData.tax) || 0
        };
        await axios.post(`${API}/users`, createData);
        toast.success('User created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !isActive });
      toast.success(`User ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      mobile: user.mobile || user.email?.replace('@staff.mipay.uk', '') || '',
      full_name: user.full_name,
      password: '',
      role: user.role,
      permissions: user.permissions || { ...DEFAULT_PERMISSIONS },
      nas_access: user.nas_access || false, // NAS module access
      nas_permissions: user.nas_permissions || { ...DEFAULT_NAS_PERMISSIONS },
      // SR Incentives
      sr_incentives_enabled: user.sr_incentives_enabled || false,
      sr_incentives_rate: user.sr_incentives_rate || '',
      sr_incentives_total: user.sr_incentives_total || '',
      // Pay settings
      salary_type: user.salary_type || 'daily',
      half_month_salary: user.half_month_salary || '',
      daily_rate: user.daily_rate || '',
      overtime_rate: user.overtime_rate || '',
      hours_per_day: user.hours_per_day || '8',
      sss: user.sss || '',
      philhealth: user.philhealth || '',
      pagibig: user.pagibig || '',
      tax: user.tax || '',
      // Location settings
      assigned_office_id: user.assigned_office_id || '',
      clock_in_radius_km: user.clock_in_radius_km || 2,
      allow_remote_clock: user.allow_remote_clock || false
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      mobile: '',
      full_name: '',
      password: '',
      role: 'staff',
      permissions: { ...DEFAULT_PERMISSIONS },
      nas_access: false, // NAS module access
      nas_permissions: { ...DEFAULT_NAS_PERMISSIONS },
      // SR Incentives
      sr_incentives_enabled: false,
      sr_incentives_rate: '',
      sr_incentives_total: '',
      // Pay settings
      salary_type: 'daily',
      half_month_salary: '',
      daily_rate: '',
      overtime_rate: '',
      hours_per_day: '8',
      sss: '',
      philhealth: '',
      pagibig: '',
      tax: '',
      // Location settings
      assigned_office_id: '',
      clock_in_radius_km: 2,
      allow_remote_clock: false
    });
    setEditingUser(null);
  };

  const handlePermissionChange = (permKey, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permKey]: checked
      }
    }));
  };

  const getRoleBadge = (role) => {
    return role === 'admin'
      ? 'bg-purple-100 text-purple-700 border-purple-200'
      : 'bg-blue-100 text-blue-700 border-blue-200';
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-slate-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-jakarta font-bold text-slate-900">User Management</h2>
          <p className="text-slate-600 mt-1">Manage admin and staff users</p>
        </div>
        <div className="flex gap-2">
          {/* Hidden file input for import */}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
          />
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            title="Download import template"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            <Upload size={18} className="mr-2" />
            {importing ? 'Importing...' : 'Import'}
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={exporting || users.length === 0}
          >
            <Download size={18} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn">
                <Plus size={20} className="mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser ? 'Update user information below.' : 'Create a new user account.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    disabled={editingUser}
                    data-testid="user-username-input"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  placeholder="09XX XXX XXXX"
                  data-testid="user-mobile-input"
                />
              </div>
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  data-testid="user-fullname-input"
                />
              </div>
              <div>
                <Label htmlFor="password">{editingUser ? 'New Password (leave empty to keep current)' : 'Password *'}</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="staff">Staff (Custom Permissions)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* NAS Module Access */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="nas_access"
                    checked={formData.nas_access || false}
                    onCheckedChange={(checked) => setFormData({ ...formData, nas_access: checked })}
                    data-testid="nas-access-checkbox"
                  />
                  <div>
                    <label htmlFor="nas_access" className="text-sm font-semibold text-green-800 cursor-pointer">
                      🏪 NAS Access (Nenen's Agrivet Supply)
                    </label>
                    <p className="text-xs text-green-600">Enable access to the NAS inventory management system</p>
                  </div>
                </div>
                
                {/* NAS Access Buttons - All feature controls for staff */}
                {formData.nas_access && formData.role === 'staff' && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <Label className="text-sm font-semibold text-green-800 mb-3 block">NAS Access Areas - Click to toggle</Label>
                    <div className="grid grid-cols-4 gap-3">
                      
                      {/* NAS Shop Access */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_shop_access: !formData.nas_permissions?.nas_shop_access
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_shop_access
                            ? 'bg-blue-100 border-blue-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">🛒</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_shop_access ? 'text-blue-700' : 'text-slate-600'
                        }`}>NAS Shop</p>
                        {formData.nas_permissions?.nas_shop_access && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* POS Access */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_pos_access: !formData.nas_permissions?.nas_pos_access
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_pos_access
                            ? 'bg-green-100 border-green-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-green-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">💳</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_pos_access ? 'text-green-700' : 'text-slate-600'
                        }`}>POS</p>
                        {formData.nas_permissions?.nas_pos_access && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Products View */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_view_products: !formData.nas_permissions?.nas_view_products
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_view_products
                            ? 'bg-cyan-100 border-cyan-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-cyan-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">📦</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_view_products ? 'text-cyan-700' : 'text-slate-600'
                        }`}>View Products</p>
                        {formData.nas_permissions?.nas_view_products && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Products Add/Edit */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_edit_products: !formData.nas_permissions?.nas_edit_products
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_edit_products
                            ? 'bg-teal-100 border-teal-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-teal-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">✏️</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_edit_products ? 'text-teal-700' : 'text-slate-600'
                        }`}>Add/Edit Products</p>
                        {formData.nas_permissions?.nas_edit_products && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-teal-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Bodega Orders */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_bodega_orders: !formData.nas_permissions?.nas_bodega_orders
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_bodega_orders
                            ? 'bg-orange-100 border-orange-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">🚚</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_bodega_orders ? 'text-orange-700' : 'text-slate-600'
                        }`}>Bodega Orders</p>
                        {formData.nas_permissions?.nas_bodega_orders && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-orange-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Bodega Stock/Inventory */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_inventory: !formData.nas_permissions?.nas_inventory
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_inventory
                            ? 'bg-amber-100 border-amber-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-amber-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">🏭</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_inventory ? 'text-amber-700' : 'text-slate-600'
                        }`}>Bodega Stock</p>
                        {formData.nas_permissions?.nas_inventory && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-amber-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Customers */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_customers: !formData.nas_permissions?.nas_customers
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_customers
                            ? 'bg-indigo-100 border-indigo-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">👥</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_customers ? 'text-indigo-700' : 'text-slate-600'
                        }`}>Customers</p>
                        {formData.nas_permissions?.nas_customers && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Customer Orders */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_customer_orders: !formData.nas_permissions?.nas_customer_orders
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_customer_orders
                            ? 'bg-violet-100 border-violet-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-violet-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">📋</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_customer_orders ? 'text-violet-700' : 'text-slate-600'
                        }`}>Customer Orders</p>
                        {formData.nas_permissions?.nas_customer_orders && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-violet-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Deliveries */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_deliveries: !formData.nas_permissions?.nas_deliveries
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_deliveries
                            ? 'bg-rose-100 border-rose-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-rose-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">🚛</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_deliveries ? 'text-rose-700' : 'text-slate-600'
                        }`}>Deliveries</p>
                        {formData.nas_permissions?.nas_deliveries && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-rose-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Payments */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_payments: !formData.nas_permissions?.nas_payments
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_payments
                            ? 'bg-emerald-100 border-emerald-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-emerald-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">💵</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_payments ? 'text-emerald-700' : 'text-slate-600'
                        }`}>Payments</p>
                        {formData.nas_permissions?.nas_payments && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Reports */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_reports: !formData.nas_permissions?.nas_reports
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_reports
                            ? 'bg-sky-100 border-sky-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-sky-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">📊</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_reports ? 'text-sky-700' : 'text-slate-600'
                        }`}>Reports</p>
                        {formData.nas_permissions?.nas_reports && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-sky-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* SR Incentives */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_sr_incentives: !formData.nas_permissions?.nas_sr_incentives
                          },
                          sr_incentives_enabled: !formData.nas_permissions?.nas_sr_incentives
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_sr_incentives
                            ? 'bg-purple-100 border-purple-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">💰</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_sr_incentives ? 'text-purple-700' : 'text-slate-600'
                        }`}>SR Incentives</p>
                        {formData.nas_permissions?.nas_sr_incentives && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                      {/* Order Approver - Special permission */}
                      <button
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          nas_permissions: {
                            ...formData.nas_permissions,
                            nas_order_approver: !formData.nas_permissions?.nas_order_approver
                          }
                        })}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          formData.nas_permissions?.nas_order_approver
                            ? 'bg-red-100 border-red-500 shadow-md'
                            : 'bg-white border-slate-200 hover:border-red-300'
                        }`}
                      >
                        <div className="text-2xl mb-1">✅</div>
                        <p className={`font-semibold text-xs ${
                          formData.nas_permissions?.nas_order_approver ? 'text-red-700' : 'text-slate-600'
                        }`}>Order Approver</p>
                        {formData.nas_permissions?.nas_order_approver && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">✓</span>
                        )}
                      </button>
                      
                    </div>
                    
                    <div className="flex gap-2 mt-3 pt-3 border-t border-green-200">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs border-green-400 text-green-700 hover:bg-green-100"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            nas_permissions: {
                              nas_shop_access: true,
                              nas_pos_access: true,
                              nas_view_products: true,
                              nas_edit_products: true,
                              nas_bodega_orders: true,
                              nas_inventory: true,
                              nas_customers: true,
                              nas_customer_orders: true,
                              nas_deliveries: true,
                              nas_payments: true,
                              nas_reports: true,
                              nas_sr_incentives: true,
                              nas_order_approver: true
                            },
                            sr_incentives_enabled: true
                          });
                        }}
                      >
                        Enable All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-xs border-red-400 text-red-700 hover:bg-red-100"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            nas_permissions: {},
                            sr_incentives_enabled: false
                          });
                        }}
                      >
                        Disable All
                      </Button>
                    </div>
                  </div>
                )}
                
                {formData.nas_access && formData.role === 'admin' && (
                  <p className="mt-3 text-xs text-green-600">Admin users have full NAS access to all areas.</p>
                )}
              </div>
              
              {/* SR Incentives Settings - Only show when SR Incentives is enabled */}
              {formData.nas_access && formData.sr_incentives_enabled && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <Label className="text-sm font-semibold text-purple-800 mb-3 block">
                    💰 SR Incentives Settings
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium text-purple-700">Incentive Rate (₱/sack)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.sr_incentives_rate || ''}
                        onChange={(e) => setFormData({ ...formData, sr_incentives_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="border-purple-300 focus:border-purple-500"
                        data-testid="sr-incentives-rate"
                      />
                      <p className="text-xs text-purple-500 mt-1">Amount earned per sack sold</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-purple-700">Total Accumulated (₱)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.sr_incentives_total || ''}
                        onChange={(e) => setFormData({ ...formData, sr_incentives_total: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="border-purple-300 focus:border-purple-500 bg-purple-100"
                        data-testid="sr-incentives-total"
                      />
                      <p className="text-xs text-purple-500 mt-1">Total incentives earned to date</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Permission Checkboxes - Only show for staff */}
              {formData.role === 'staff' && (
                <div className="space-y-4 border-t pt-4 mt-4">
                  <Label className="text-base font-semibold">Permissions</Label>
                  <p className="text-sm text-slate-500">Select what this staff member can do</p>
                  
                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group.title} className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">{group.title}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {group.permissions.map((permKey) => (
                          <div key={permKey} className="flex items-center space-x-2">
                            <Checkbox
                              id={permKey}
                              checked={formData.permissions[permKey] || false}
                              onCheckedChange={(checked) => handlePermissionChange(permKey, checked)}
                              data-testid={`perm-${permKey}`}
                            />
                            <label
                              htmlFor={permKey}
                              className="text-sm text-slate-600 cursor-pointer"
                            >
                              {PERMISSION_LABELS[permKey]}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.role === 'admin' && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700">
                    <strong>Admin users</strong> have full access to all features and can manage other users.
                  </p>
                </div>
              )}

              {/* Pay Settings Section */}
              {formData.role === 'staff' && (
                <div className="space-y-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm font-semibold text-emerald-800">💰 Pay Settings (for Payslip)</p>
                  
                  {/* Salary Type Selector */}
                  <div className="mb-3">
                    <Label className="text-xs font-medium">Salary Type</Label>
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, salary_type: 'daily' })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.salary_type === 'daily' 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Daily Rate
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, salary_type: 'monthly' })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.salary_type === 'monthly' 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Monthly Salary
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Daily Rate fields */}
                    {formData.salary_type === 'daily' && (
                      <>
                        <div>
                          <Label htmlFor="daily_rate" className="text-xs">Daily Rate (₱)</Label>
                          <Input
                            id="daily_rate"
                            type="number"
                            value={formData.daily_rate}
                            onChange={(e) => setFormData({ ...formData, daily_rate: e.target.value })}
                            placeholder="e.g., 500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Pay per day for 8 hours</p>
                        </div>
                        <div>
                          <Label htmlFor="hours_per_day" className="text-xs">Regular Hours/Day</Label>
                          <Input
                            id="hours_per_day"
                            type="number"
                            value={formData.hours_per_day}
                            onChange={(e) => setFormData({ ...formData, hours_per_day: e.target.value })}
                            placeholder="8"
                          />
                          <p className="text-xs text-gray-500 mt-1">Hours before overtime</p>
                        </div>
                        <div>
                          <Label htmlFor="overtime_rate" className="text-xs">Overtime Rate (₱/hour)</Label>
                          <Input
                            id="overtime_rate"
                            type="number"
                            value={formData.overtime_rate}
                            onChange={(e) => setFormData({ ...formData, overtime_rate: e.target.value })}
                            placeholder="e.g., 80"
                          />
                          <p className="text-xs text-gray-500 mt-1">Pay per hour beyond regular</p>
                        </div>
                      </>
                    )}

                    {/* Monthly Salary fields */}
                    {formData.salary_type === 'monthly' && (
                      <>
                        <div>
                          <Label htmlFor="half_month_salary" className="text-xs">Half Month Salary (₱)</Label>
                          <Input
                            id="half_month_salary"
                            type="number"
                            value={formData.half_month_salary}
                            onChange={(e) => setFormData({ ...formData, half_month_salary: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label htmlFor="overtime_rate" className="text-xs">Overtime Rate (₱/hour)</Label>
                          <Input
                            id="overtime_rate"
                            type="number"
                            value={formData.overtime_rate}
                            onChange={(e) => setFormData({ ...formData, overtime_rate: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                      </>
                    )}

                    {/* Common deductions */}
                    <div>
                      <Label htmlFor="sss" className="text-xs">SSS (₱)</Label>
                      <Input
                        id="sss"
                        type="number"
                        value={formData.sss}
                        onChange={(e) => setFormData({ ...formData, sss: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="philhealth" className="text-xs">PhilHealth (₱)</Label>
                      <Input
                        id="philhealth"
                        type="number"
                        value={formData.philhealth}
                        onChange={(e) => setFormData({ ...formData, philhealth: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pagibig" className="text-xs">Pag-IBIG (₱)</Label>
                      <Input
                        id="pagibig"
                        type="number"
                        value={formData.pagibig}
                        onChange={(e) => setFormData({ ...formData, pagibig: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax" className="text-xs">Tax (₱)</Label>
                      <Input
                        id="tax"
                        type="number"
                        value={formData.tax}
                        onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Location Settings - Only show when editing */}
              {editingUser && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    Location Settings (Geofencing)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="assigned_office" className="text-xs">Assigned Office</Label>
                      <Select 
                        value={formData.assigned_office_id || 'none'} 
                        onValueChange={(value) => setFormData({ ...formData, assigned_office_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select office" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No office assigned</SelectItem>
                          {offices.map(office => (
                            <SelectItem key={office.id} value={office.id}>
                              {office.name} - {office.address?.substring(0, 30)}...
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {offices.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          No offices created yet. Go to Accounting → Office Locations to add offices first.
                        </p>
                      )}
                      {offices.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          To add new offices, go to Accounting → Office Locations
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="clock_in_radius" className="text-xs">Clock-in Radius (km)</Label>
                      <Input
                        id="clock_in_radius"
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="50"
                        value={formData.clock_in_radius_km}
                        onChange={(e) => setFormData({ ...formData, clock_in_radius_km: parseFloat(e.target.value) || 2 })}
                      />
                      <p className="text-xs text-slate-500 mt-1">How close to the office the employee must be to clock in</p>
                    </div>

                    <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                      <div>
                        <Label className="text-sm">Allow Remote Clock-in</Label>
                        <p className="text-xs text-slate-500">Skip location check for this employee</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${formData.allow_remote_clock ? 'text-green-600' : 'text-slate-400'}`}>
                          {formData.allow_remote_clock ? 'Yes' : 'No'}
                        </span>
                        <Switch
                          checked={formData.allow_remote_clock}
                          onCheckedChange={(checked) => setFormData({ ...formData, allow_remote_clock: checked })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="submit-user-btn">
                  {editingUser ? 'Update' : 'Create'} User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Username</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Mobile</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">NAS Access</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{user.full_name}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-700 font-mono text-sm">{user.username}</td>
                    <td className="px-6 py-4 text-slate-700">{user.mobile || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        user.role === 'admin' || user.nas_access
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>
                        {user.role === 'admin' ? '✓ Admin' : (user.nas_access ? '✓ Enabled' : '✗ Disabled')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        user.is_active
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-red-100 text-red-700 border-red-200'
                      }`}>
                        {user.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleActive(user.id, user.is_active)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.is_active
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={user.is_active ? 'Disable user' : 'Enable user'}
                          data-testid={`toggle-user-${user.id}`}
                        >
                          {user.is_active ? <UserX size={18} /> : <UserCheck size={18} />}
                        </button>
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                          title="Edit user permissions"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          disabled={user.id === currentUser.id}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Users;
